// react
import { ReactNode, createContext, useEffect, useState } from 'react';

import axios from 'api/axios';

// third-party
import _ from 'lodash';

// types
import { RentalPlan, RentalOption } from 'types';

// style

type DeliverySetting = { label: string; delivery: number; fee: number };

export type ConfigContextType = {
  isLocal: boolean;
  isDev: boolean;
  isProd: boolean;
  websiteId: number;
  shopHolidays: string[];
  deliverySetting: DeliverySetting[];
  rentalPlans: RentalPlan[];
  rentalOptions: RentalOption[];
};

type ConfigProviderProps = {
  children: ReactNode;
};

const getShopHolidays = async (): Promise<string[]> => {
  const { data: response } = await axios.get('/endpoint/shops/holidays/1');
  const { holidays } = response.data;
  return holidays;
};

const getDeliverySetting = async (): Promise<DeliverySetting[]> => {
  const { data: response } = await axios.get('/endpoint/shops/delivery-setting');
  const { deliverySetting } = response.data;
  return deliverySetting;
};

const getRentalPlans = async (): Promise<RentalPlan[]> => {
  const { data: response } = await axios.get('/endpoint/rental/get-plans');
  const { plans } = response.data;
  return _.map(plans, (plan) => {
    return {
      id: plan.id,
      model_name: plan.model_name,
      model_description: plan.model_description,
      name: plan.name,
      image_path: plan.image_path,
      description: plan.description,
      gb: plan.gb,
      prices: JSON.parse(plan.rental_plan_prices[0].prices)
    };
  });
};

const getRentalOptions = async (): Promise<RentalOption[]> => {
  const { data: response } = await axios.get('/endpoint/rental/get-options');
  const { options } = response.data;

  return _.map(options, (option) => {
    return {
      id: option.id,
      name: option.name,
      description: option.description,
      prices: JSON.parse(option.rental_option_prices[0].prices)
    };
  });
};

const initialValue = {
  isLocal: process.env.REACT_APP_ENV_NAME === 'Local',
  isDev: process.env.REACT_APP_ENV_NAME === 'Development',
  isProd: process.env.REACT_APP_ENV_NAME === 'Production',
  websiteId: Number(process.env.REACT_APP_WEBSITE_ID),
  shopHolidays: [],
  deliverySetting: [],
  rentalPlans: [],
  rentalOptions: []
};

const ConfigContext = createContext<ConfigContextType>(initialValue);

export const ConfigProvider = ({ children }: ConfigProviderProps) => {
  const [configContext, setConfigContext] = useState<ConfigContextType>(initialValue);
  useEffect(() => {
    (async () => {
      const shopHolidays = await getShopHolidays();
      const deliverySetting = await getDeliverySetting();
      const rentalPlans = await getRentalPlans();
      const rentalOptions = await getRentalOptions();
      setConfigContext({ ...configContext, shopHolidays, deliverySetting, rentalPlans, rentalOptions });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <ConfigContext.Provider value={configContext}>{children}</ConfigContext.Provider>;
};

export default ConfigContext;
